/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: authoring-common
 * (C) Copyright IBM Corp. 2020, 2021
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
	Button,
	FlexItem,
	FlexLayout,
	Flyout,
	Label,
	Link,
	SVGIcon
} from '@ba/ba-ui-carbon-toolkit';
import reset32 from '@ba/ba-graphics/dist/icons-js/reset_32';

import StringResources from 'nls/StringResources';
import CustomColorDialog from './CustomColorDialog';
import '../../stylesheets/all.scss';
import KeyCodes from '../lib/@waca/core-client/js/core-client/ui/KeyCodes';

const colorPickerName = 'colorPicker';

/**
 * Description:
 * The client is responsible for supplying at least the required properties. The client is also responsible for
 * supplying the favorite colors from the paletteService if there is one as well. The selected color is returned
 * in the callback and the client must set the selectedColor property with it and presist the selected color.
 * This is needed for the Color Picker to show which color is current selected from palette and/or favorites.
 *
 * This ColorPicker has 5 required properties:
 *   colors - An array of colors (strings of hex values that start with #)
 *   colorsLabel - The label of the set of colours that are shown in the flyout
 *   onColorSelected - A callback prototype which has one parameter, which is the selected color that is returns back.
 *   paletteService - the paletteService object which is needed by the Custom Color dialog for selecting favorite/custom colors
 *   resetColor - A callback funciton that is called when the "restor to default" action is executed. The implementer is responsible for
 *      keeping track of the default colour and how to restore it.
 *
 * */
export default class ColorPicker extends Component {
	static propTypes = {
		/* Colors to be selected */
		colors: PropTypes.array.isRequired,
		/* The callback that gets called on click of a color; onColorSelected(colorHexValue) */
		onColorSelected: PropTypes.func.isRequired,
		/* Palette Service needed to create custom color dialog */
		paletteService: PropTypes.object.isRequired,
		/* Colors label. It has a limited size, width of the control */
		colorsLabel: PropTypes.string.isRequired,
		/* The callback that handles restoring the default colour (defined by the implementer) */
		resetColor: PropTypes.func.isRequired,
		/* The title for each color in the colors property */
		colorTitles: PropTypes.array.isRequired,

		/* Custom css class names */
		className: PropTypes.string,
		/* Flag to disable the color picker button */
		disabled: PropTypes.bool,
		/* Favorite colors to be selected. The palette service has a method to retrieve this called getFavoriteColors() */
		favoriteColors: PropTypes.array,
		/* See ba-ui-carbon-toolkit Flyout placement values; we default to "bottomRightStart" or "bottomLeftStart" for rtl languages */
		orientation: Flyout.propTypes.placement,
		/* Selected color to show on the button. NOTE: Must be set by client when a color is selected. Would be passed to the onColorSelected() */
		selectedColor: PropTypes.string,
		/* Show a button to allow user to add a custom colour */
		showCustomColorButton: PropTypes.bool,
		/* Custom css styles. Applied to the component container div */
		style: PropTypes.object,
		/* Tooltip when you hover on the color picker button */
		title: PropTypes.string
	};

	static defaultProps = {
		className: '',
		colorFlyoutOpen: false,
		customColorDialogOpen: false,
		disabled: false
	};

	classPrefix = 'clsColorPicker';

	constructor(props) {
		super(props);
		this.state = {
			...ColorPicker.defaultProps
		};
		this._handleOpen = this._handleOpen.bind(this);
		this.triggerButton = React.createRef();
		this.attachRef = React.createRef();
	}

	render() {
		const { colorFlyoutOpen, customColorDialogOpen } = this.state;
		const { selectedColor, title, disabled, className, style } = this.props;

		let orientation = this.props.orientation;
		if (!this.props.orientation) {
			orientation = (document.body.dir === 'rtl' ? 'bottomLeftStart' : 'bottomRightStart');
		}

		return (
			<div className={`${this.classPrefix} ${className}`} style={style}>
				{ customColorDialogOpen && this._renderCustomColor() }
				<Button
					aria-controls={`${colorPickerName}_button`}
					data-tid={`${colorPickerName}_button`}
					title={title}
					disabled={disabled}
					tabIndex={0}
					ref={this.triggerButton}
					data-name={this._selectedDataName()}
					data-value={selectedColor}
					kind='ghost'
					onClick={this._handleOpen}
					style={this._getColorStyle(selectedColor) }
					className={`${this.classPrefix}_button`}
				/>
				{colorFlyoutOpen &&
					<Flyout
						data-tid={`${colorPickerName}_flyout`}
						placement={orientation}
						domNodeToAttachTo={this.attachRef.current}
						hideArrow
						offset={2}
						onClose={this._handleClose.bind(this)}
						className={`${this.classPrefix}_flyout`}
					>
						{this._displayFlyout()}
					</Flyout>
				}
			</div>
		);
	}

	_selectedDataName () {
		const { selectedColor, favoriteColors, colorTitles, colors } = this.props;
		if (colorTitles && colors.includes(selectedColor)) {
			return colorTitles[colors.findIndex((e) => e === selectedColor)];
		} else if (favoriteColors && favoriteColors.includes(selectedColor)) {
			return 'favColor' + favoriteColors.findIndex((e) => e === selectedColor);
		} else {
			return 'customColor';
		}
	}

	_displayFlyout () {
		const { colors, colorsLabel, favoriteColors, colorTitles } = this.props;
		return (
			<FlexLayout direction='column' className={`${this.classPrefix}_flyoutContent`}>
				<FlexLayout className={`${this.classPrefix}_paletteHeader`}>
					<FlexItem>
						<Label label={colorsLabel} />
					</FlexItem>
					<SVGIcon
						className={`${this.classPrefix}_restoreDefault`}
						size='small'
						iconId={reset32.id}
						tabIndex='0'
						alt={StringResources.get('restoreDefault')}
						ariaLabelledby={StringResources.get('restoreDefault')}
						onClick={this._restoreDefault.bind(this)}>
					</SVGIcon>
				</FlexLayout>
				<FlexLayout className={`${this.classPrefix}_paletteContent`}>
					{colors.map((color, index) => this._displayColors(color, index, colorTitles))}
				</FlexLayout>
				{ favoriteColors && favoriteColors.length > 1 &&
				<FlexLayout direction='column' className={`${this.classPrefix}_paletteHeader`}>
					<FlexItem>
						<Label label={StringResources.get('favorite')} />
					</FlexItem>
					<FlexLayout className={`${this.classPrefix}_paletteContent`}>
						{favoriteColors.map((color, index) => this._displayColors(color, index))}
					</FlexLayout>
				</FlexLayout>
				}
				{ this.props.showCustomColorButton &&
				<FlexItem>
					<Link onClick={this._showCustomColor.bind(this, true)}>{StringResources.get('modeCustom')}</Link>
				</FlexItem>
				}
			</FlexLayout>
		);
	}

	_displayColors (color, index, titles) {
		const { selectedColor, favoriteColors } = this.props;
		let style = this._getColorStyle(color);
		if (style) {
			style.background = style.backgroundColor;
		}

		let clsName;
		if (selectedColor === color) {
			clsName = `${this.classPrefix}_selected_indicator`;
		} else {
			clsName = `${this.classPrefix}_cell`;
		}
		let title;
		if (titles) {
			title = titles[index];
		} else if (favoriteColors) {
			title = 'favColor' + index;
		}
		return (
			<FlexItem
				className={clsName}
				style={style}
				key={index}
				tabIndex={0}
				data-name={title}
				data-value={color}
				title={title}
				onClick={this._handleSelect.bind(this, color)}
				onKeyDown={(e) => this._handleKey(e, color)}
			/>
		);
	}

	_handleOpen() {
		this.setState({ colorFlyoutOpen: !this.state.colorFlyoutOpen });
	}

	_handleClose() {
		const { customColorDialogOpen } = this.state;
		// hide this if the color dialog isn't open
		if (!customColorDialogOpen) {
			this.setState({ colorFlyoutOpen: false });
		}
	}

	_getColorStyle(color) {
		if (!color) {
			return;
		}

		return {
			color,
			backgroundColor: color
		};
	}

	_handleSelect(color) {
		this.props.onColorSelected(color);
		this.setState({ colorFlyoutOpen: false });
	}

	_showCustomColor(show) {
		this.setState({ customColorDialogOpen: show });
	}

	_renderCustomColor() {
		const { paletteService, selectedColor } = this.props;

		return <CustomColorDialog
			key='colorDialog'
			selectedColor={selectedColor}
			paletteService={paletteService}
			isDialogOpen={true}
			onChange={this._handleSelect.bind(this)}
			onCloseDialog={this._showCustomColor.bind(this, false)}
		/>;
	}

	_restoreDefault() {
		this.props.resetColor();
	}

	_handleKey(e, color) {
		// if Enter or Space key
		if ((e.keyCode === KeyCodes.ENTER) || (e.keyCode === KeyCodes.SPACE)) {
			this._handleSelect(color);
			e.preventDefault();
		}
	}

}



// WEBPACK FOOTER //
// ./src/js/dialog/ColorPicker.js